import { Component, computed, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { AccordionModule } from 'primeng/accordion';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { DialogComponent } from 'src/app/@core/components/ui/dialog/dialog.component';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import {
  GeotechnicalAnalysisService,
  IGeneralWork,
  IGeotechnicalVendorAnalysis,
  IStabilizationWork,
  IVendorField,
  IVendorProjectInfo,
  IVendorProjectSource,
  IWorkCostEntry,
  IWorkCostMaterial,
  IWorkCostEquipment,
  IWorkCostLabour,
  VENDOR_ANALYSIS_STATUS,
} from 'src/app/features/geotechnical-analysis/services/geotechnical-analysis.service';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { ButtonComponent } from 'src/app/@core/components/ui/button/button.component';
import { InputComponent } from 'src/app/@core/components/ui/input/input.component';
import { DataTableComponent } from 'src/app/@shared/components/data-table/data-table.component';
import { TableColumn, TableConfig } from 'src/app/@shared/models/table.models';
import {
  VENDOR_MATERIALS_COLUMNS,
  VENDOR_LABOURS_COLUMNS,
  VENDOR_EQUIPMENTS_COLUMNS,
  VENDOR_MATERIALS_TABLE_CONFIG,
} from './vendor-analysis-table.config';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { SocketService, SOCKET_EVENTS } from 'src/app/@core/services/socket.service';
import { IAttachment, IUser } from 'src/app/@core/models/model';
import { OnlyOfficeDocEditorComponent } from 'src/app/@shared/components/only-office-doc-editor/only-office-doc-editor.component';
import { ONLY_OFFICE_EDITOR_TYPE } from 'src/app/@core/models/enums';
import { JWTService } from 'src/app/contract-management/services/jwt.service';
import { environment } from 'src/environments/environment.dev';

declare var Api: any;

interface ProjectInfoRow {
  property: string;
  value: string;
  source: IVendorProjectSource;
}

interface FlatMaterialRow extends IWorkCostMaterial {
  workName: IVendorField<string | null>;
}

interface FlatLabourRow extends IWorkCostLabour {
  workName: IVendorField<string | null>;
}

interface FlatEquipmentRow extends IWorkCostEquipment {
  workName: IVendorField<string | null>;
}

@Component({
  selector: 'app-vendor-analysis-tab',
  standalone: true,
  imports: [CommonModule, FormsModule, TooltipModule, InputTextModule, AccordionModule, DialogComponent, IconComponent, ButtonComponent, DataTableComponent, OnlyOfficeDocEditorComponent],
  templateUrl: './vendor-analysis-tab.component.html',
  styleUrl: './vendor-analysis-tab.component.scss',
})
export class VendorAnalysisTabComponent implements OnInit, OnDestroy {
  VENDOR_ANALYSIS_STATUS = VENDOR_ANALYSIS_STATUS;

  @ViewChild('sourceCellTpl') sourceCellTpl!: TemplateRef<any>;
  @ViewChild('valueCellTpl') valueCellTpl!: TemplateRef<any>;
  @ViewChild('workNameCellTpl') workNameCellTpl!: TemplateRef<any>;
  @ViewChild('descriptionCellTpl') descriptionCellTpl!: TemplateRef<any>;
  @ViewChild('productionRateCellTpl') productionRateCellTpl!: TemplateRef<any>;
  @ViewChild('productionRateUnitCellTpl') productionRateUnitCellTpl!: TemplateRef<any>;
  @ViewChild('workCostNameCellTpl') workCostNameCellTpl!: TemplateRef<any>;
  @ViewChild('materialCellTpl') materialCellTpl!: TemplateRef<any>;
  @ViewChild('wasteFactorCellTpl') wasteFactorCellTpl!: TemplateRef<any>;
  @ViewChild('unitWeightCellTpl') unitWeightCellTpl!: TemplateRef<any>;
  @ViewChild('unitCostCellTpl') unitCostCellTpl!: TemplateRef<any>;
  @ViewChild('labourCellTpl') labourCellTpl!: TemplateRef<any>;
  @ViewChild('equipmentCellTpl') equipmentCellTpl!: TemplateRef<any>;
  @ViewChild('unitCostPerHrCellTpl') unitCostPerHrCellTpl!: TemplateRef<any>;
  @ViewChild('taxOtPercentCellTpl') taxOtPercentCellTpl!: TemplateRef<any>;
  @ViewChild('quantityCellTpl') quantityCellTpl!: TemplateRef<any>;
  @ViewChild('truncateCellTpl') truncateCellTpl!: TemplateRef<any>;

  private geoReportDataService = inject(GeoTechnicalAnalysisReportDataService);
  private geotechnicalService = inject(GeotechnicalAnalysisService);
  private authService = inject(AuthenticationService);
  private socketService = inject(SocketService);
  private jwtService = new JWTService();

  private vendorAnalysisUpdatedSub: Subscription | null = null;

  currentUser: IUser | null = null;

  data = signal<IGeotechnicalVendorAnalysis | null>(null);
  loading = signal(false);
  error = signal('');

  readonly tableConfig: TableConfig = { paginator: false, emptyMessage: 'No data' };
  readonly materialsTableConfig = VENDOR_MATERIALS_TABLE_CONFIG;

  readonly projectInfoColumns: TableColumn[] = [
    { field: 'property', header: 'Property' },
    { field: 'value', header: 'Value', type: 'custom', template: 'valueCell' },
  ];

  readonly worksColumns: TableColumn[] = [
    { field: 'name', header: 'Name', type: 'custom', template: 'workNameCell' },
    { field: 'description', header: 'Description', type: 'custom', template: 'descriptionCell' },
    { field: 'productionRate', header: 'Production Rate', type: 'custom', template: 'productionRateCell' },
    { field: 'productionRateUnit', header: 'Unit', type: 'custom', template: 'productionRateUnitCell' },
  ];

  status = computed(() => this.data()?.status ?? VENDOR_ANALYSIS_STATUS.NOT_STARTED);
  isProcessing = computed(() => this.loading() || this.status() === VENDOR_ANALYSIS_STATUS.PROCESSING);
  isFailed = computed(() => !this.loading() && this.status() === VENDOR_ANALYSIS_STATUS.FAILED);
  isEmpty = computed(
    () =>
      !this.loading() &&
      !this.isFailed() &&
      this.status() === VENDOR_ANALYSIS_STATUS.NOT_STARTED
  );

  projectInfoRows = computed<ProjectInfoRow[]>(() => {
    const info: IVendorProjectInfo | undefined = this.data()?.projectInfo;
    if (!info) return [];
    const toRow = (property: string, field: IVendorProjectInfo[keyof IVendorProjectInfo]): ProjectInfoRow => ({
      property,
      value: field?.value != null ? String(field.value) : '—',
      source: { reason: field?.source?.reason ?? null, sourceType: field?.source?.sourceType ?? null, sources: field?.source?.sources ?? [] },
    });
    return [
      toRow('Project Location', info.projectLocation),
      toRow('Project Area', info.projectArea),
      toRow('Shift Hours Per Day', info.shiftHoursPerDay),
      toRow('Theoretical Budget', info.theoreticalBudget),
      toRow('Theoretical Schedule (Workdays)', info.theoreticalScheduleWorkdays),
    ];
  });

  get projectInfoCustomTemplates() {
    return { valueCell: this.valueCellTpl, sourceCell: this.sourceCellTpl };
  }

  get worksCustomTemplates() {
    return {
      workNameCell: this.workNameCellTpl,
      descriptionCell: this.descriptionCellTpl,
      productionRateCell: this.productionRateCellTpl,
      productionRateUnitCell: this.productionRateUnitCellTpl,
    };
  }

  get materialsCustomTemplates() {
    return {
      workCostNameCell: this.workCostNameCellTpl,
      materialCell: this.materialCellTpl,
      wasteFactorCell: this.wasteFactorCellTpl,
      unitWeightCell: this.unitWeightCellTpl,
      unitCostCell: this.unitCostCellTpl,
      taxOtPercentCell: this.taxOtPercentCellTpl,
    };
  }

  get laboursCustomTemplates() {
    return {
      workCostNameCell: this.workCostNameCellTpl,
      labourCell: this.labourCellTpl,
      unitCostPerHrCell: this.unitCostPerHrCellTpl,
      taxOtPercentCell: this.taxOtPercentCellTpl,
      quantityCell: this.quantityCellTpl,
    };
  }

  get equipmentsCustomTemplates() {
    return {
      workCostNameCell: this.workCostNameCellTpl,
      equipmentCell: this.equipmentCellTpl,
      unitCostPerHrCell: this.unitCostPerHrCellTpl,
      taxOtPercentCell: this.taxOtPercentCellTpl,
      quantityCell: this.quantityCellTpl,
    };
  }

  get vendorCostTemplates() {
    return { truncateCell: this.truncateCellTpl };
  }

  sourceDialogVisible = false;
  selectedSource: IVendorProjectSource | null = null;
  aiSourceIndex = 0;

  expandedTable: 'projectInfo' | 'generalWorks' | 'stabilizationWorks' | 'generalMaterials' | 'generalLabour' | 'generalEquipment' | 'stabilizationMaterials' | 'stabilizationLabour' | 'stabilizationEquipment' | null = null;

  // Tooltip constants
  readonly PROJECT_AREA_TOOLTIP = 'The total area of work considered for the project\n\nNote: Provide the project area in Square Yards (SY)';
  readonly SHIFT_HOURS_TOOLTIP = 'The number of shift hours scheduled in a standard working day, used for labor and equipment productivity calculations';
  readonly THEORETICAL_BUDGET_TOOLTIP = 'The baseline budget assumed to be required to complete the project';
  readonly THEORETICAL_SCHEDULE_TOOLTIP = 'The baseline duration in workdays assumed to be required to complete the project';
  readonly PROJECT_LOCATION_TOOLTIP = 'Geographical location of the project';

  // Project info edit dialog
  projectInfoEditVisible = false;
  projectInfoViewOnly = false;
  editProjectArea: number | null = null;
  editShiftHoursPerDay: number | null = null;
  editTheoreticalBudget: number | null = null;
  editTheoreticalScheduleWorkdays: number | null = null;
  editProjectLocation: string = '';
  savingProjectInfo = signal(false);

  get isEditProjectInfoValid(): boolean {
    return (
      this.editProjectArea !== null && this.editProjectArea > 0 &&
      this.editShiftHoursPerDay !== null && this.editShiftHoursPerDay > 0 &&
      this.editTheoreticalBudget !== null && this.editTheoreticalBudget > 0 &&
      this.editTheoreticalScheduleWorkdays !== null && this.editTheoreticalScheduleWorkdays > 0
    );
  }

  openProjectInfoEdit(): void {
    const info = this.data()?.projectInfo;
    this.editProjectArea = info?.projectArea?.value as number ?? null;
    this.editShiftHoursPerDay = info?.shiftHoursPerDay?.value as number ?? null;
    this.editTheoreticalBudget = info?.theoreticalBudget?.value as number ?? null;
    this.editTheoreticalScheduleWorkdays = info?.theoreticalScheduleWorkdays?.value as number ?? null;
    this.editProjectLocation = (info?.projectLocation?.value as unknown as string) ?? '';
    this.projectInfoViewOnly = false;
    this.projectInfoEditVisible = true;
  }

  openProjectInfoSourceClick(row: ProjectInfoRow): void {
    if (row.property === 'Project Location' && row.source?.sourceType === 'AI_EXTRACTED') {
      this.openCellSource(row.source);
      return;
    }
    const info = this.data()?.projectInfo;
    this.editProjectArea = info?.projectArea?.value as number ?? null;
    this.editShiftHoursPerDay = info?.shiftHoursPerDay?.value as number ?? null;
    this.editTheoreticalBudget = info?.theoreticalBudget?.value as number ?? null;
    this.editTheoreticalScheduleWorkdays = info?.theoreticalScheduleWorkdays?.value as number ?? null;
    this.editProjectLocation = (info?.projectLocation?.value as unknown as string) ?? '';
    this.projectInfoViewOnly = true;
    this.projectInfoEditVisible = true;
  }

  onProjectInfoSave(): void {
    if (!this.isEditProjectInfoValid || this.savingProjectInfo()) return;
    const analysisId = this.geoReportDataService.analysisReportId();
    if (!analysisId) return;

    this.savingProjectInfo.set(true);
    this.geotechnicalService.updateProjectInfo(analysisId, {
      projectArea: this.editProjectArea!,
      shiftHoursPerDay: this.editShiftHoursPerDay!,
      theoreticalBudget: this.editTheoreticalBudget!,
      theoreticalScheduleWorkdays: this.editTheoreticalScheduleWorkdays!,
      projectLocation: this.editProjectLocation?.trim() || null,
    }).subscribe({
      next: () => {
        this.savingProjectInfo.set(false);
        this.projectInfoEditVisible = false;
        this.getVendorAnalysis();
      },
      error: () => {
        this.savingProjectInfo.set(false);
        this.error.set('Failed to update project info');
      },
    });
  }

  openExpandDialog(table: typeof this.expandedTable): void {
    this.expandedTable = table;
  }

  closeExpandDialog(): void {
    this.expandedTable = null;
  }

  openSourceDialog(source: IVendorProjectSource): void {
    this.selectedSource = source;
    console.log("source=", source)
    this.aiSourceIndex = 0;
    this.sourceDialogVisible = true;
  }

  navigateSource(direction: number): void {
    const total = this.selectedSource?.sources?.length ?? 0;
    this.aiSourceIndex = Math.max(0, Math.min(this.aiSourceIndex + direction, total - 1));
  }

  generalWorks = computed<IGeneralWork[]>(() => this.data()?.generalWorks ?? []);
  stabilizationWorks = computed<IStabilizationWork[]>(() => this.data()?.stabilizationWorks ?? []);

  readonly materialsColumns = VENDOR_MATERIALS_COLUMNS;
  readonly laboursColumns = VENDOR_LABOURS_COLUMNS;
  readonly equipmentsColumns = VENDOR_EQUIPMENTS_COLUMNS;

  private flattenCosts(entries: IWorkCostEntry[]) {
    const materials: FlatMaterialRow[] = [];
    const labours: FlatLabourRow[] = [];
    const equipments: FlatEquipmentRow[] = [];
    for (const entry of entries) {
      for (const m of entry.materials) {
        materials.push({ workName: entry.workName, ...m });
      }
      for (const l of entry.labours) {
        labours.push({ workName: entry.workName, ...l });
      }
      for (const e of entry.equipments) {
        equipments.push({ workName: entry.workName, ...e });
      }
    }
    return { materials, labours, equipments };
  }

  readonly costSharedTemplates = () => ({
    workCostNameCell: this.workCostNameCellTpl,
    unitCostPerHrCell: this.unitCostPerHrCellTpl,
    taxOtPercentCell: this.taxOtPercentCellTpl,
    quantityCell: this.quantityCellTpl,
  });

  generalWorkCostFlat = computed(() => this.flattenCosts(this.data()?.generalWorkCosts ?? []));
  stabilizationWorkCostFlat = computed(() => this.flattenCosts(this.data()?.stabilizationWorkCosts ?? []));

  // Excel sheet dialog
  analysisExcelDialogVisible = false;
  activeExcelSheet = signal<string | null>(null);
  private isExcelSourcePreview = false;

  openExcelSheet(sheetName?: string): void {
    this.isExcelSourcePreview = false;
    this.activeExcelSheet.set(sheetName ?? null);
    this.geoReportDataService.refreshAnalysisExcelUrl();
    this.analysisExcelDialogVisible = true;
  }

  getFavicon(url: string): string {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
    } catch {
      return '';
    }
  }

  cleanUrl(url: string): string {
    try {
      const u = new URL(url);
      return u.hostname + (u.pathname !== '/' ? u.pathname : '');
    } catch {
      return url;
    }
  }

  onFaviconError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  openCellSource(source: IVendorProjectSource | null | undefined): void {
    if (!source) return;
    if (source.sourceType === 'ANALYSIS_EXCEL') {
      this.isExcelSourcePreview = true;
      this.geoReportDataService.refreshAnalysisExcelUrl();
      this.analysisExcelDialogVisible = true;
    } else {
      this.openSourceDialog(source);
    }
  }

  onAnalysisExcelDialogHide(): void {
    this.activeExcelSheet.set(null);
    if (this.isExcelSourcePreview) {
      this.isExcelSourcePreview = false;
      return;
    }
    this.vendorAnalysisUpdatedSub = this.socketService
      .on<{ geotechnicalAnalysisId: string; success: boolean; error?: string }>(
        SOCKET_EVENTS.GEOTECHNICAL_VENDOR_ANALYSIS_UPDATED
      )
      .subscribe((payload) => {
        const data = Array.isArray(payload) ? payload[0] : payload;
        this.vendorAnalysisUpdatedSub?.unsubscribe();
        this.vendorAnalysisUpdatedSub = null;
        if (data?.success) {
          this.getVendorAnalysis();
        } else {
          this.error.set(data?.error ?? 'Failed to sync vendor analysis');
        }
      });
  }

  onAnalysisExcelReady(): void {
    const sheetName = this.activeExcelSheet();
    console.log(sheetName)
    if (sheetName) {
      this.openSpecificSheet(sheetName);
    }
  }

  private openSpecificSheet(sheetName: string): void {
    console.log(sheetName)
    const analysisId = this.geoReportDataService.analysisReportId();
    if (!analysisId) return;
    const editorId = `analysis_excel_vendor_${analysisId}`;
    const editorInstance = (window as any)['DocEditor']?.instances?.[editorId];
    const spreadsheetApi = editorInstance?.asc_spreadsheetApi;
    if (!spreadsheetApi) return;
    (window as any).Asc.scope.targetSheet = sheetName;
    spreadsheetApi.callCommand(function () {
      const oWorksheet = Api.GetSheet(Asc.scope.targetSheet);
      if (oWorksheet) {
        oWorksheet.SetActive();
      }
    });
  }

  analysisExcelPreviewFile = computed<IAttachment | null>(() => {
    const analysisReport = this.geoReportDataService.analysisReport();
    const analysisId = this.geoReportDataService.analysisReportId();
    const analysisFileUrl = analysisReport?.analysisExcelUrl?.trim();
    if (!analysisId || !analysisReport || !analysisFileUrl) return null;
    const analysisName = analysisReport.displayName || analysisReport.name || 'Analysis Excel';
    return {
      _id: analysisId,
      filename: `${analysisName}.xlsx`,
      url: analysisFileUrl,
      fullUrl: analysisFileUrl,
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      isPublic: false,
    };
  });

  analysisExcelEditorConfig = computed<IConfig | null>(() => {
    const previewFile = this.analysisExcelPreviewFile();
    const analysisId = this.geoReportDataService.analysisReportId();
    const canEdit = this.geoReportDataService.permissions()?.canEdit ?? false;
    const sheetName = this.activeExcelSheet();
    if (!previewFile || !analysisId) return null;

    const docUrl = previewFile.fullUrl ?? previewFile.url;
    const versionHash = this.urlVersionHash(docUrl);
    // Include sheet name in key so OnlyOffice reinitializes when target sheet changes
    const sheetSuffix = sheetName ? `-${sheetName.replace(/\s+/g, '_')}` : '';

    const tokenConfig: Partial<IConfig> = {
      document: {
        fileType: 'xlsx',
        key: `${analysisId}-analysis-excel-${versionHash}${sheetSuffix}`,
        title: previewFile.filename,
        url: docUrl,
        permissions: {
          chat: canEdit,
          comment: canEdit,
          download: true,
          edit: canEdit,
          fillForms: canEdit,
          modifyContentControl: canEdit,
          modifyFilter: canEdit,
          print: true,
          protect: canEdit,
          review: canEdit,
          userInfoGroups: [],
        },
      },
      documentType: ONLY_OFFICE_EDITOR_TYPE.EXCEL,
      editorConfig: {
        user: {
          group: analysisId,
          id: this.currentUser?.email ?? '',
          image: this.currentUser?.profilePicture?.url
            ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}`
            : '',
          name: `${this.currentUser?.firstName ?? ''} ${this.currentUser?.lastName ?? ''}`.trim(),
        },
        embedded: { toolbarDocked: 'top' },
        ...(sheetName ? { actionLink: { action: { type: 'sheet', data: sheetName } } } : {}),
        customization: {
          toolbarHideFileName: true,
          hideRightMenu: true,
          compactToolbar: true,
          compactHeader: true,
          comments: canEdit,
          forcesave: true,
          autosave: false,
          uiTheme: 'theme-gray',
          logo: { image: `${environment.s3Url}/inndoc-ai/assets/images/3.png` },
          zoom: -2,
        },
        callbackUrl: `${environment.apiUrl}/public/save-geotechnical-analysis-excel?analysisId=${analysisId}&userId=${this.currentUser?._id ?? ''}`,
      },
      height: '100%',
      width: '100%',
    };

    return {
      ...tokenConfig,
      token: this.jwtService.signToken(tokenConfig, environment.jwtSecret),
    } as IConfig;
  });

  private urlVersionHash(url: string): string {
    const versionMatch = url.match(/[?&]versionId=([^&]+)/);
    if (versionMatch) return versionMatch[1].slice(0, 16);
    let hash = 0;
    for (let i = 0; i < url.length; i++) {
      hash = (Math.imul(31, hash) + url.charCodeAt(i)) | 0;
    }
    return Math.abs(hash).toString(36);
  }

  constructor() {
    this.authService.currentUser.subscribe((user) => {
      this.currentUser = user ?? null;
    });
  }

  ngOnInit(): void {
    this.getVendorAnalysis();
  }

  getVendorAnalysis(): void {
    const analysisId = this.geoReportDataService.analysisReportId();
    if (!analysisId) return;

    this.loading.set(true);
    this.error.set('');

    this.geotechnicalService.getVendorAnalysis(analysisId).subscribe({
      next: (res) => {
        this.data.set(res.data ?? null);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Failed to load vendor analysis data');
        this.loading.set(false);
      },
    });
  }

  ngOnDestroy(): void {
    this.vendorAnalysisUpdatedSub?.unsubscribe();
  }
}
